import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Dumbbell } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STUDIOS = ["Tuzla", "Kartal", "Sancaktepe"];
const SERVICES = ["Functional Training", "Personal Training", "Pilates", "EMS", "Boxing / Kickboxing", "Hyrox", "Hybrid Training"];

export default function TrialForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", studio: "Tuzla", service_interest: "", company: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, phone: form.phone, studio: form.studio, service_interest: form.service_interest, company: form.company };
      if (form.email) payload.email = form.email;
      await api.post("/leads/public", payload);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0E] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        {done ? (
          <div className="text-center rounded-3xl bg-[#13131C] border border-[#222230] p-8" data-testid="trial-success">
            <div className="w-16 h-16 rounded-2xl bg-[#10B981]/15 flex items-center justify-center mx-auto mb-4"><CheckCircle2 size={32} className="text-[#10B981]" /></div>
            <h2 className="font-display font-extrabold text-2xl text-white">Başvurun Alındı! 🎉</h2>
            <p className="text-sm text-[#A0A0B2] mt-2">Ekibimiz en kısa sürede seninle iletişime geçecek. Get Up Fit'e hoş geldin!</p>
            <Button onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", studio: "Tuzla", service_interest: "" }); }} className="mt-6 bg-[#1B1B27] hover:bg-[#252536] text-white">Yeni Başvuru</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl bg-[#13131C] border border-[#222230] p-6 sm:p-8" data-testid="trial-form">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full bg-[#FF5500] text-white mb-3"><Dumbbell size={12} /> Ücretsiz Deneme</span>
            <h1 className="font-display font-black text-2xl sm:text-3xl text-white leading-tight">Deneme Antrenmanı <span className="text-[#FF5500]">Talep Et</span></h1>
            <p className="text-sm text-[#A0A0B2] mt-2 mb-6">Formu doldur, seni arayalım ve ilk antrenmanını planlayalım.</p>
            <div className="space-y-3">
              <input type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Ad Soyad *</label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="trial-name" className="bg-[#0E0E14] border-[#262636] h-11 text-white focus-visible:ring-[#FF5500]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#A0A0B2] mb-1 block">Telefon *</label><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="trial-phone" className="bg-[#0E0E14] border-[#262636] h-11 text-white focus-visible:ring-[#FF5500]" /></div>
                <div><label className="text-xs text-[#A0A0B2] mb-1 block">E-posta</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="trial-email" className="bg-[#0E0E14] border-[#262636] h-11 text-white focus-visible:ring-[#FF5500]" /></div>
              </div>
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Şube</label>
                <Select value={form.studio} onValueChange={(v) => setForm({ ...form, studio: v })}>
                  <SelectTrigger data-testid="trial-studio" className="bg-[#0E0E14] border-[#262636] h-11 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white">{STUDIOS.map((s) => <SelectItem key={s} value={s}>Get Up Fit {s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">İlgilendiğin Hizmet</label>
                <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                  <SelectTrigger data-testid="trial-service" className="bg-[#0E0E14] border-[#262636] h-11 text-white"><SelectValue placeholder="Seç (opsiyonel)" /></SelectTrigger>
                  <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white">{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <Button type="submit" disabled={saving || !form.name || !form.phone} data-testid="trial-submit" className="w-full h-11 bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl mt-2">
                {saving ? <Loader2 className="animate-spin" size={18} /> : "Başvuruyu Gönder"}
              </Button>
            </div>
          </form>
        )}
        <p className="text-center text-xs text-[#68687B] mt-4">Zaten üye misin? <a href="/login" className="text-[#FF5500] font-semibold">Giriş yap</a></p>
      </div>
    </div>
  );
}
